import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { eventsAPI } from '@/services';
import { useToast } from '../components/Toast';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../contexts/AuthContext';
import BackToCalendarButton from '../components/details/BackToCalendarButton';
import InfoTile from '../components/details/InfoTile';
import {
  MemberMobileShell,
  MemberMobileHero,
} from '../components/MemberMobileShell';
import { isMemberLikeEntreaUser } from '../utils/iamRoles';

const ActivityDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { error } = useToast();
  const { role } = usePermissions();
  const { user } = useAuth();
  const hasMemberProfileLink = !!(user?.member_id || user?.person_id);
  const isMembre = isMemberLikeEntreaUser(role, hasMemberProfileLink);
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState(null);

  useEffect(() => {
    const loadActivity = async () => {
      try {
        setLoading(true);
        const response = await eventsAPI.getActivityById(id);
        setActivity(response.data || null);
      } catch (err) {
        console.error('Failed to load activity details:', err);
        error(err.response?.data?.detail || 'Erreur lors du chargement de l’activité');
        navigate('/calendar');
      } finally {
        setLoading(false);
      }
    };
    loadActivity();
  }, [id, navigate, error]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return String(dateString);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center bg-gradient-to-b from-[#f2f6fb] to-[#eef3f8] py-16">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-[#113a5b]" />
          <p className="mt-4 text-sm text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center bg-gradient-to-b from-[#f2f6fb] to-[#eef3f8] px-4 py-16">
        <div className="text-center">
          <p className="text-slate-700">Activité introuvable</p>
          <button
            type="button"
            onClick={() => navigate('/calendar')}
            className="mt-4 min-h-[44px] rounded-lg bg-[#113a5b] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#0f3350] md:text-base"
          >
            Retour au calendrier
          </button>
        </div>
      </div>
    );
  }

  const dateValue = activity.planned_date || activity.scheduled_at || activity.actual_date || activity.created_at;
  const hasPoster = Boolean(activity.poster_url);
  const typeLabel = activity.activity_kind === 'meeting'
    ? 'Réunion'
    : (activity.activity_kind === 'deadline' || activity.activity_kind === 'milestone')
      ? 'Échéance'
      : 'Activité';

  const activityIntroDescription = [typeLabel, formatDate(dateValue)].filter(Boolean).join(' · ') || 'Détails de l’activité';

  return (
    <div className="min-h-[60vh] bg-gradient-to-b from-[#f2f6fb] to-[#eef3f8] md:bg-gradient-to-br md:from-gray-50 md:via-white md:to-indigo-50/30">
      <div className="mx-auto max-w-6xl px-3 py-4 md:px-4 md:py-8">
        <div className="mb-4 md:mb-6">
          <BackToCalendarButton onClick={() => navigate('/calendar')} />
        </div>

        {isMembre && (
          <MemberMobileShell>
            <MemberMobileHero
              label="Activité"
              title={activity.title}
              hint={activityIntroDescription}
              tone="info"
              actions={
                activity.location ? (
                  <span className="mm-cta mm-cta--ghost min-h-[40px] w-full text-center text-xs">
                    {activity.location}
                  </span>
                ) : null
              }
            />
          </MemberMobileShell>
        )}

        <div className="mb-8 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
          <div className="flex flex-col lg:flex-row">
            {hasPoster && (
              <div className="lg:w-2/5 flex-shrink-0 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-50">
                <img
                  src={process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}${activity.poster_url}` : activity.poster_url}
                  alt={activity.title}
                  className="h-full min-h-[11rem] w-full object-cover md:min-h-[300px] lg:min-h-[520px]"
                />
              </div>
            )}

            <div className={`flex-1 p-4 md:p-8 lg:p-10 ${hasPoster ? 'lg:border-l border-gray-200' : ''}`}>
              <div className={`mb-8 ${isMembre ? 'hidden md:block' : ''}`}>
                <div className="flex flex-wrap items-start gap-3 mb-6">
                  <h1 className="text-3xl md:text-4xl font-bold text-gray-900 flex-1 leading-tight">{activity.title}</h1>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1.5 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800 border border-indigo-200">
                      {typeLabel}
                    </span>
                    {activity.status && (
                      <span className="px-3 py-1.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-800 border border-gray-200">
                        {activity.status}
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-sm md:text-base">
                  <InfoTile
                    tone="indigo"
                    title="Date prévue"
                    icon={(
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                      </svg>
                    )}
                  >
                    {formatDate(dateValue)}
                  </InfoTile>

                  <InfoTile
                    tone="blue"
                    title="Lieu"
                    icon={(
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                    )}
                  >
                    {activity.location || 'Non défini'}
                  </InfoTile>

                  {activity.meeting_mode && (
                    <InfoTile
                      tone="green"
                      title="Mode de réunion"
                      icon={(
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                        </svg>
                      )}
                    >
                      {activity.meeting_mode}
                    </InfoTile>
                  )}

                  {activity.meeting_link && (
                    <InfoTile
                      tone="pink"
                      title="Lien de réunion"
                      icon={(
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                        </svg>
                      )}
                    >
                      <a href={activity.meeting_link} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 font-medium inline-flex items-center gap-1">
                        Rejoindre la réunion
                      </a>
                    </InfoTile>
                  )}
                </div>
              </div>

              <div className="border-t border-gray-200 pt-8 mt-8">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Description</h2>
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed text-base">
                  {activity.description || 'Aucune description'}
                </p>
              </div>

              {!isMembre ? (
                <div className="mt-6 border-t border-gray-200 pt-6">
                  <button
                    type="button"
                    onClick={() => navigate(`/meetings?event_id=${encodeURIComponent(activity.id)}`)}
                    className="inline-flex items-center gap-2 rounded-lg bg-cyan-700 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-cyan-800"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m-7 4h10a2 2 0 002-2V8l-6-6H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    {activity.activity_kind === 'meeting' ? 'Gérer le procès-verbal' : 'Gérer le rapport de l’activité'}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityDetails;
